import app from 'flarum/common/app';

let initialized = false;

const OtelHelper = {
    init() {
        if (initialized) return;
        initialized = true;

        // app.forum is not yet populated during initializer execution (boot() sets it after all initializers run).
        // Defer to next tick so app.forum.attribute() is available.
        setTimeout(() => {
            try {
                this.doInit();
            } catch (e) {
                // Never break the app for tracing
            }
        });
    },

    doInit() {
        const enabled = app.forum.attribute<boolean>('tanka-connector.otelEnabled');
        if (!enabled) return;

        const endpoint = app.forum.attribute<string>('tanka-connector.otelExporterEndpoint');
        const serviceName = app.forum.attribute<string>('tanka-connector.otelServiceName') || 'flarum-browser';

        if (!endpoint) return;

        this.setupTracing(endpoint, serviceName).catch((e) => {
            console.warn('[OtelHelper] Failed to initialize tracing:', e);
        });
    },

    async setupTracing(endpoint: string, serviceName: string) {
        const [
            { WebTracerProvider },
            { BatchSpanProcessor },
            { OTLPTraceExporter },
            { ZoneContextManager },
            { Resource },
            { ATTR_SERVICE_NAME },
            { registerInstrumentations },
            { XMLHttpRequestInstrumentation },
            { DocumentLoadInstrumentation },
            { W3CTraceContextPropagator },
        ] = await Promise.all([
            import('@opentelemetry/sdk-trace-web'),
            import('@opentelemetry/sdk-trace-base'),
            import('@opentelemetry/exporter-trace-otlp-http'),
            import('@opentelemetry/context-zone'),
            import('@opentelemetry/resources'),
            import('@opentelemetry/semantic-conventions'),
            import('@opentelemetry/instrumentation'),
            import('@opentelemetry/instrumentation-xml-http-request'),
            import('@opentelemetry/instrumentation-document-load'),
            import('@opentelemetry/core'),
        ]);

        const resource = new Resource({
            [ATTR_SERVICE_NAME]: serviceName,
        });

        const exporter = new OTLPTraceExporter({
            url: endpoint.replace(/\/$/, '') + '/v1/traces',
        });

        const provider = new WebTracerProvider({
            resource,
            spanProcessors: [new BatchSpanProcessor(exporter)],
        });

        const apiUrl = app.forum.attribute<string>('apiUrl') || '';
        const origin = window.location.origin;
        const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const allowedUrls: RegExp[] = [new RegExp(escapeRegex(origin))];
        if (apiUrl) {
            allowedUrls.push(new RegExp(escapeRegex(apiUrl)));
        }

        provider.register({
            contextManager: new ZoneContextManager(),
            propagator: new W3CTraceContextPropagator(),
        });

        const stripQueryFromSpan = (_span: any, xhr: XMLHttpRequest) => {
            const urlAttr = _span.attributes?.['http.url'] || _span.attributes?.['url.full'];
            if (urlAttr && typeof urlAttr === 'string' && urlAttr.includes('?')) {
                const cleanUrl = urlAttr.split('?')[0];
                _span.setAttribute('http.url', cleanUrl);
                _span.setAttribute('url.full', cleanUrl);
            }
        };

        registerInstrumentations({
            instrumentations: [
                new DocumentLoadInstrumentation(),
                new XMLHttpRequestInstrumentation({
                    propagateTraceHeaderCorsUrls: allowedUrls,
                    applyCustomAttributesOnSpan: stripQueryFromSpan,
                }),
            ],
        });
    },
};

export default OtelHelper;
